// src/pages/clab-biodata-pending/components/RejectionModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { rejectionReasonAPI } from 'utils/api';

const RejectionModal = ({ type, biodataIds, onClose, onConfirm }) => {
  const [selectedReasonIds, setSelectedReasonIds] = useState([]);
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReasons = async () => {
      const result = await rejectionReasonAPI.getAllRejectionReasons();
      setRejectionReasons((result.data && result.data.data) || []);
    };
    fetchReasons();
  }, []);

  // Check if "Other" is selected
  const isOtherSelected = selectedReasonIds.some(id => {
    const reason = rejectionReasons.find(r => r.id === parseInt(id));
    return reason && (reason.reason === 'Other' || reason.name === 'Other');
  });

  // Toggle reason selection
  const toggleReason = (reasonId) => {
    setSelectedReasonIds(prev => {
      if (prev.includes(reasonId)) {
        return prev.filter(id => id !== reasonId);
      } else {
        return [...prev, reasonId];
      }
    });
  };

  const handleConfirm = async () => {
    if (selectedReasonIds.length === 0) {
      setError('Please select at least one reason');
      return;
    }

    // If "Other" is selected, require custom reason
    if (isOtherSelected && !customReason.trim()) {
      setError('Please provide additional details when "Other" is selected');
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm({
        reason_ids: selectedReasonIds,  // Send array of reason IDs
        comments: customReason.trim() || undefined
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const count = Array.isArray(biodataIds) ? biodataIds.length : 1;

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-bold text-text-primary">
              {type === 'bulk' ? 'Bulk Reject Biodata' : 'Reject Biodata'}
            </h2>
            <p className="text-text-secondary mt-1">
              {type === 'bulk' 
                ? `Rejecting ${count} biodata submissions` 
                : 'Rejecting 1 biodata submission'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-600 hover:bg-secondary-100 rounded-md transition-colors duration-150"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-secondary mb-3">
              Reasons for Rejection <span className="text-xs text-text-tertiary">(Select all that apply)</span>
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-secondary-200 rounded-md p-3">
              {(rejectionReasons || []).map(reason => (
                <label
                  key={reason.id}
                  className="flex items-start space-x-3 p-2 hover:bg-secondary-50 rounded-md cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedReasonIds.includes(reason.id)}
                    onChange={() => toggleReason(reason.id)}
                    className="mt-1 h-4 w-4 text-primary focus:ring-primary border-secondary-300 rounded"
                  />
                  <span className="text-sm text-text-primary flex-1">
                    {reason.name || reason.reason}
                  </span>
                </label>
              ))}
            </div>
            {error && (
              <p className="text-xs text-error mt-2">{error}</p>
            )}
          </div>

          {isOtherSelected && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Additional Details <span className="text-error">*</span>
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Please provide specific details about why this biodata is being rejected..."
                className="input-field w-full h-24 resize-none"
                required
              />
              <p className="text-xs text-text-secondary mt-1">
                This information will be sent to the recruiter to help them understand what needs to be corrected.
              </p>
            </div>
          )}

          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <h4 className="font-medium text-warning-800 mb-1">Confirmation Required</h4>
                <p className="text-sm text-warning-700">
                  {type === 'bulk' 
                    ? `This action will reject ${count} biodata submissions. The recruiters will be notified and can make corrections before resubmission.`
                    : 'This action will reject the biodata submission. The recruiter will be notified and can make corrections before resubmission.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 bg-secondary-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-secondary-700 bg-surface border border-secondary-300 rounded-md hover:bg-secondary-50 transition-colors duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || selectedReasonIds.length === 0 || (isOtherSelected && !customReason.trim())}
            className="flex items-center space-x-2 px-6 py-2 bg-error text-white rounded-md hover:bg-error-600 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting && <Icon name="Loader2" size={16} className="animate-spin" />}
            <span>{isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;